'use client'

import { useEffect, useState, type RefObject, type Dispatch, type SetStateAction } from 'react'
import { PlayerInterface } from '@foundation-player/loader'
import { ga4Event } from '@/libs/Google/GoogleTagManager.lib'
import { getBreakpoint } from '@/utils/app/detect'
import { type VideoContext } from '@/types/cms'
import { type VideoPlayerProps, type VideoStickyState } from '@/types/video'
import { isPurMode } from '@/utils/app/paid'
import { RequestCookie } from 'next/dist/compiled/@edge-runtime/cookies'

export const createPlayerProps = ({
  context,
  autoplay,
  heartbeatStarttype,
  cookies,
}: {
  context: VideoContext
  autoplay?: boolean
  heartbeatStarttype?: 0 | 1
  cookies: RequestCookie[]
}): VideoPlayerProps => ({
  id:
    context.model.fields['old-id'] && context.model.fields['old-id'].length > 0
      ? context.model.fields['old-id']
      : context.model.id,
  title: context.model.kicker + ': ' + context.model.title,
  publishedAt: context.model.publishedAt,
  lengthSeconds: context.model.durationMillis / 1000,
  mode: context.model.fields.mode,
  ...(context.model.image ? { poster: context.model.image.baseUrl + 'Img_16_9/920/image.webp' } : {}),
  ...(context.model.tags ? { keywords: context.model.tags } : {}),
  ...(context.model.fields['no-ads'] === 'true' || isPurMode(cookies) ? { disableAds: true } : {}),
  trackingPath: context.model.meta.trackingPath,
  category: context.model.meta.videoAdZone,
  autoplay: autoplay ?? false,
  heartbeatStarttype: heartbeatStarttype ?? 0,
  source: {
    ...(context.model.video['web-prog'] ? { progressive: context.model.video['web-prog'] } : {}),
    ...(context.model.video['web-dash'] ? { dash: context.model.video['web-dash'] } : {}),
    ...(context.model.video['web-hls'] ? { hls: context.model.video['web-hls'] } : {}),
  },
})

export const useTracking = (stickyState: VideoStickyState, category: string, videoId?: string) => {
  useEffect(() => {
    if (stickyState.trigger) {
      let action = ''

      if (stickyState.isSticky) {
        action = 'start'
      } else {
        switch (stickyState.trigger) {
          case 'close':
            action = 'closeByButton'
            break
          case 'scroll':
            action = 'closeByScroll'
            break
          case 'sessionEnd':
            action = 'closeByVideoEnd'
            break
        }
      }

      ga4Event({
        eventcategory: category,
        eventaction: action,
        eventlabel: window.location.href,
        eventvalue: videoId,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stickyState.isSticky])
}

export const useSticky = (
  playerWrapper: RefObject<HTMLDivElement | null>,
  player: PlayerInterface | undefined,
): [VideoStickyState, Dispatch<SetStateAction<VideoStickyState>>] => {
  const [stickyState, setStickyState] = useState<VideoStickyState>({
    isSticky: false,
    trigger: undefined,
  })
  useEffect(() => {
    const handleScroll = () => {
      if (!player) {
        return
      }
      if (playerWrapper.current) {
        // @ts-ignore
        const rect = playerWrapper.current.getBoundingClientRect()

        setStickyState(
          rect.top < 40 &&
            player.isPlaying() &&
            ((getBreakpoint() === 'xs' && !globalThis.window.matchMedia('(orientation: landscape)').matches) ||
              getBreakpoint() !== 'xs')
            ? { isSticky: true, trigger: 'scroll' }
            : { isSticky: false, trigger: 'scroll' },
        )
      }
    }

    if (player) {
      document.addEventListener('scroll', handleScroll)
    }

    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player])

  return [stickyState, setStickyState]
}
